import type { ApiFehler } from "./typen";
import { gibtText } from "../i18n";
import { eingabeGrenze, eingabeMail, eingabeUstId, eingabeZuLang, eingabeZuViele, endgrundAus, kontextAusgang, schluesselOhneHuelle, werteAusDaten } from "./fehlerwerte";

/* ══════════════════════════════════════════════════════════════════
   Der Weg zur API.

   Zwei Sachen sind hier nicht verhandelbar:

   **Das Kontext-Token lebt im Speicher, nie im `localStorage`.** Ein
   Token, das im Speicher des Browsers liegt, ist mit dem Schließen des
   Reiters weg. Eines im `localStorage` liegt dort weiter und ist für
   jedes Skript lesbar, das je auf diese Herkunft gelangt. Was den
   Wiedereinstieg trägt, ist das Cookie `mm_sitzung` — `httpOnly`, also
   für kein Skript sichtbar, auch nicht für unseres.

   **Bei `401 { fehler: "kontext.abgelaufen" }` wird genau einmal
   nachgeholt.** Der Abfangjäger holt ein neues Token über
   `POST /api/kontext` und wiederholt die Anfrage. Genau einmal: Wer
   in einer Schleife nachholt, baut aus einer abgelaufenen Sitzung
   einen Sturm auf den Server.
   ══════════════════════════════════════════════════════════════════ */

/** Die Basis. Über den Vite-Proxy bleibt `/api` gleiche Herkunft — nur
 *  so geht das Sitzungscookie mit (siehe vite.config.ts). */
const BASIS = "/api";

/** Ob mit Attrappen gearbeitet wird. `apps/api` entsteht parallel; bis
 *  sie steht, liefert `daten/attrappe` dieselben Formen. */
export const ATTRAPPE = import.meta.env.VITE_ATTRAPPE === "1";

/* ---------- Das Token, im Speicher ---------- */

let kontext: string | null = null;
let laeuftAb: string | null = null;

/** Wer erfahren will, dass die Sitzung zu Ende ist — die Schale meldet
 *  sich hier an und schaltet dann auf die Anmeldung zurück. */
type Zuhoerer = () => void;
const beiSitzungsende = new Set<Zuhoerer>();

export function kontextSetzen(neu: string | null, ab?: string | null): void {
  kontext = neu;
  laeuftAb = ab ?? null;
  if (neu) endgrund = null;
}

export function kontextDa(): boolean {
  return kontext !== null;
}

export function kontextLaeuftAb(): string | null {
  return laeuftAb;
}

export function aufSitzungsende(hoerer: Zuhoerer): () => void {
  beiSitzungsende.add(hoerer);
  return () => beiSitzungsende.delete(hoerer);
}

/**
 * Warum die Sitzung zuletzt endete, wenn der Server es sagte (A-09). Gilt bis
 * zur nächsten Anmeldung — `kontextSetzen` räumt ihn ab.
 */
let endgrund: string | null = null;

/** Der Grund des letzten Sitzungsendes, solange niemand wieder angemeldet ist. */
export function sitzungsendeGrund(): string | null {
  return endgrund;
}

function sitzungIstAus(grund: string | null = null): void {
  endgrund = grund;
  kontext = null;
  laeuftAb = null;
  beiSitzungsende.forEach((h) => h());
}

/* ---------- Der Fehler, den die Oberfläche kennt ---------- */

/**
 * Ein Fehler mit Schlüssel und Daten — nie mit fertigem Satz.
 *
 * Die Beschriftung macht `i18n/de.json`; `daten` trägt mit, welche
 * Maschine und welcher Zeitraum gemeint waren. Ohne das steht die
 * Oberfläche vor „geht nicht" (34-api.md, Abschnitt „Fehler").
 */
export class Fehler extends Error {
  readonly schluessel: string;
  readonly status: number;
  readonly daten: Record<string, unknown>;

  constructor(schluessel: string, status: number, daten: Record<string, unknown> = {}) {
    super(schluessel);
    this.name = "Fehler";
    this.schluessel = schluessel;
    this.status = status;
    this.daten = daten;
  }

  /**
   * Der i18n-Pfad zu diesem Fehler.
   *
   *   `belegung.ueberschneidung` → `fehler.belegungUeberschneidung`
   *   `nicht_gefunden`           → `fehler.nichtGefunden`
   *   `eingabe.plz`              → `fehler.eingabe` (ein Satz für alle Felder;
   *                                welches Feld es war, zeigt das Feld selbst)
   *   `eingabe.regel`            → `fehler.eingabeRegel`
   *
   * **Drei `eingabe.*` nennen kein Feld**, sondern kommen aus der Datenbank:
   * `regel` (Check-Constraint), `verweis` (Fremdschlüssel), `format`. Für sie
   * hieß es bis zum 16.09.2026 ebenfalls „Prüfe das Feld, das rot umrandet
   * ist" — und es war keines umrandet, weil es keines gab.
   */
  get textPfad(): string {
    /* Wer im Weg liegt (T-27): Mit dem Vorgang heißt der Satz „… schon
       vergeben — fest für V-2026-0090." Ohne ihn bleibt der bisherige. */
    if (this.schluessel === "belegung.ueberschneidung" && typeof this.daten["vorgang"] === "string"
      && typeof this.daten["maschine"] === "string") {
      return "fehler.belegungUeberschneidungVorgang";
    }
    if (this.schluessel === "eingabe.regel") return "fehler.eingabeRegel";
    if (this.schluessel === "eingabe.verweis") return "fehler.eingabeVerweis";
    if (this.schluessel === "eingabe.format") return "fehler.eingabeFormat";
    /* Der Schichtfaktor hat seinen eigenen Satz (F1, 04.10.2026): „höchstens
       3,0 — 15 ist zu viel". Der allgemeine Bereichssatz sagte „0 bis 3",
       und 0 ist gerade nicht erlaubt. */
    if (this.schluessel === "eingabe.schichtfaktor" && typeof this.daten["hoechstens"] === "number") {
      return "fehler.eingabeSchichtfaktor";
    }
    if (eingabeZuLang(this.schluessel, this.daten)) return "fehler.eingabeZuLang";
    if (eingabeZuViele(this.schluessel, this.daten)) return "fehler.eingabeZuViele";
    const grenze = eingabeGrenze(this.schluessel, this.daten);
    if (grenze) return grenze;
    const ustId = eingabeUstId(this.schluessel, this.daten);
    if (ustId) return ustId;
    const mail = eingabeMail(this.schluessel, this.daten);
    if (mail) return mail === "mehrere" ? "fehler.eingabeMailMehrere" : "fehler.eingabeMail";
    if (this.schluessel.startsWith("eingabe.")) return "fehler.eingabe";
    /* **Zwei Familien haben ihre Sätze an ihrer Maske** (P9-01, 05.10.2026):
       `zeit.*` unter `einsatzzeit.fehler`, `optionen.*` unter
       `optionen.fehler`. Bis heute wussten das nur die Masken selbst
       (`zeitFehlerPfad`, `positionsFehlerPfad`); am Telefon kam ein
       abgewiesener Schritt über die Warteschlange — und dort stand
       `fehler.zeitNichtHeute` statt des Satzes. Jetzt weiß es der Fehler. */
    if (this.schluessel.startsWith("zeit.")) return `einsatzzeit.fehler.${this.schluessel.slice(5)}`;
    if (this.schluessel.startsWith("optionen.")) {
      return `optionen.fehler.${this.schluessel.slice(9).split("_")
        .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1))).join("")}`;
    }
    const teile = this.schluessel.split(/[._]/).filter(Boolean);
    if (!teile.length) return "fehler.unbekannt";
    const name = teile
      .map((t, i) => (i === 0 ? t : t.charAt(0).toUpperCase() + t.slice(1)))
      .join("");
    /* **Ein Schlüssel, den Lexware Office erfunden hat** (Systemprüfung
       B-07). `packages/lex` macht aus dem `i18nKey` einer Ablehnung einen
       eigenen Schlüssel — `lexware.invalid_value`, `lexware.missing_entity`.
       Für die hat de.json keinen Satz, und in der Maske stand dann der Pfad.
       Sie sind alle dasselbe: drüben abgelehnt. Was genau, sagt die Zeile
       darunter (`FehlerBand`, `lexware_antwort`). */
    if (this.schluessel.startsWith("lexware.") && !gibtText(`fehler.${name}`)) {
      return "fehler.lexwareNichtAnnehmbar";
    }
    /* **Einzahl, wo die Zahl 1 ist** (P9-06, 05.10.2026): „1 Punkte halten
       die Abrechnung an". Bringt der Fehler `zahl` oder `anzahl` = 1 mit und
       steht daneben ein Satz `…Einer`, gilt der. */
    if ((Number(this.daten["zahl"]) === 1 || Number(this.daten["anzahl"]) === 1) && gibtText(`fehler.${name}Einer`)) {
      return `fehler.${name}Einer`;
    }
    return `fehler.${name}`;
  }

  /**
   * Die Werte für den Satz — `{sekunden}`, `{felder}`, `{maschine}`.
   *
   * Steht am Fehler selbst, damit niemand sie durchreichen muss: Bis zum
   * 03.10.2026 tat das jede Maske für sich, und wo sie es vergaß, stand der
   * Platzhalter im Satz (A-04, K-15, D-25; siehe `daten/fehlerwerte.ts`).
   */
  get werte(): Record<string, string | number> {
    return werteAusDaten(this.daten);
  }
}

/* ---------- Die eine Stelle, an der gesprochen wird ---------- */

type Ruf = {
  weg: string;
  /* `PUT` seit dem 15.09.2026, für genau einen Weg:
     `PUT /einstellungen/lexware` ersetzt den Zugang ganz, statt ihn
     teilweise zu ändern. Ein PATCH auf ein Geheimnis wäre die falsche
     Zusage — es gibt keinen halben Schlüssel. */
  art?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  koerper?: unknown;
  /** Anfragen ohne Token — Anmeldung und Kontextholen. */
  ohneKontext?: boolean;
};

async function einmal({ weg, art = "GET", koerper, ohneKontext }: Ruf): Promise<Response> {
  const kopf: Record<string, string> = {};
  if (koerper !== undefined) kopf["Content-Type"] = "application/json";
  if (!ohneKontext && kontext) kopf["Authorization"] = `Bearer ${kontext}`;

  return fetch(`${BASIS}${weg}`, {
    method: art,
    headers: kopf,
    // Das Sitzungscookie muss mit — sonst kennt `POST /api/kontext`
    // niemanden, und der Nachholversuch scheitert immer.
    credentials: "same-origin",
    ...(koerper !== undefined ? { body: JSON.stringify(koerper) } : {}),
  });
}

/**
 * Versucht beim Start, die Sitzung aus dem Cookie wiederherzustellen.
 *
 * Das Kontext-Token liegt absichtlich nur im Speicher und ist nach einem
 * Neuladen weg. Das Sitzungscookie `mm_sitzung` ist es nicht — es ist
 * `httpOnly` und überlebt. Ohne diesen Versuch stünde nach jedem F5 die
 * Anmeldung da, obwohl die Sitzung serverseitig noch läuft; das ist der
 * Unterschied zwischen „sicher" und „unbenutzbar".
 *
 * `aus` ist kein Fehler, sondern die Auskunft „niemand angemeldet" — dann
 * gehört die Anmeldung hin. `netz` heißt: Ob jemand angemeldet ist, ließ sich
 * nicht fragen. Die Schale fragt dann noch einmal, sobald das Netz zurück
 * ist (`schale/Sitzung.tsx`), statt bei der Anmeldung stehen zu bleiben.
 */
export async function kontextWiederherstellen(): Promise<"da" | "aus" | "netz"> {
  if (ATTRAPPE) return "aus";
  return kontextNachholen();
}

/**
 * Holt ein neues Kontext-Token. Läuft die Sitzung selbst aus, ist Schluss —
 * **aber nur dann** (A-10, D-50, T-41; `kontextAusgang` in
 * `daten/fehlerwerte.ts`). Ohne Netz oder bei einer Zwischenstation, die
 * `502` sagt, bleibt die Sitzung stehen: Das alte Token läuft vielleicht ab,
 * aber die nächste Anfrage holt mit `kontext.abgelaufen` ein neues, sobald
 * wieder Empfang ist.
 */
async function kontextNachholen(): Promise<"da" | "aus" | "netz"> {
  let antwort: Response;
  try {
    antwort = await einmal({ weg: "/kontext", art: "POST", ohneKontext: true });
  } catch {
    return "netz";
  }
  const ausgang = kontextAusgang(antwort.status);
  if (ausgang === "aus") {
    let grund: string | null = null;
    try {
      grund = endgrundAus(await antwort.json());
    } catch {
      /* Ohne lesbaren Rumpf kein Grund. */
    }
    sitzungIstAus(grund);
    return "aus";
  }
  if (ausgang === "netz") return "netz";
  try {
    const daten = (await antwort.json()) as { kontext: string; laeuft_ab: string };
    kontextSetzen(daten.kontext, daten.laeuft_ab);
    return "da";
  } catch {
    // Eine 200 ohne lesbaren Rumpf kam nicht von der API.
    return "netz";
  }
}

/* ══════════════ Eine Antwort ohne Hülle ══════════════

   **Nicht jede Antwort kommt von der API.** Zwischen Browser und `apps/api`
   steht nginx, und wenn es selbst antwortet, antwortet es in HTML — eine
   Fehlerseite mit `<html>` darin, kein `{ fehler: "…" }`.

   Bis zum 21.09.2026 lief die Zeile `JSON.parse(text)` darauf auf, warf einen
   `SyntaxError`, und der ist kein `Fehler`: Er trägt keinen Schlüssel, und
   jede Maske, die `e instanceof Fehler` fragt, fiel auf „Das hat nicht
   geklappt" zurück. Luca beim Hochladen einer großen Datei: *„Ich kann das
   aktuell gar nicht, da kommt dann eine Fehlermeldung."*

   Der Fall, der das auslöst, ist **einer**, und er betrifft **jeden** Upload:
   `413 Request Entity Too Large`. Eine Datei reist als Base64 in einem
   JSON-Rumpf (`ablageHochladen`), und Base64 macht aus drei Bytes vier —
   25 MiB Datei sind rund 33,4 MiB Rumpf. Stand `client_max_body_size` auf
   derselben Zahl wie die Dateigrenze, wies nginx alles über rund 18,6 MiB ab,
   bevor die API es je sah.

   **Deshalb steht es hier und nicht in der Dokumentenmaske.** Foto,
   Unterschrift, Beleg und Import gehen durch dieselbe Tür; einen Satz nur für
   Dokumente zu bauen hieße, ihn beim nächsten Mal wieder zu bauen. */
const ohneHuelle = schluesselOhneHuelle;

async function auswerten<T>(antwort: Response): Promise<T> {
  if (antwort.status === 204) return undefined as T;
  const text = await antwort.text();

  let inhalt: unknown = null;
  if (text) {
    try {
      inhalt = JSON.parse(text);
    } catch {
      throw new Fehler(ohneHuelle(antwort.status), antwort.status);
    }
  }

  if (!antwort.ok) {
    const f = (inhalt ?? {}) as ApiFehler;
    throw new Fehler(f.fehler || "unbekannt", antwort.status, f.daten ?? {});
  }
  return inhalt as T;
}

/**
 * Eine Anfrage, mit genau einem Nachholversuch.
 *
 * Nachgeholt wird nur bei `401 kontext.abgelaufen`. Ein `401
 * kontext.fehlt` heißt: Es war nie eines da — dann ist der Weg die
 * Anmeldung, nicht die Wiederholung.
 */
export async function ruf<T>(anfrage: Ruf): Promise<T> {
  let antwort: Response;
  try {
    antwort = await einmal(anfrage);
  } catch {
    throw new Fehler("netz", 0);
  }

  if (antwort.status === 401 && !anfrage.ohneKontext) {
    const text = await antwort.clone().text();
    let schluessel = "";
    try {
      schluessel = (JSON.parse(text) as ApiFehler).fehler ?? "";
    } catch {
      /* Eine 401 ohne Hülle — dann wird nicht nachgeholt. */
    }

    const nachgeholt = schluessel === "kontext.abgelaufen" ? await kontextNachholen() : null;
    if (nachgeholt === "da") {
      // Genau einmal. Scheitert auch das, geht der Fehler durch.
      try {
        antwort = await einmal(anfrage);
      } catch {
        throw new Fehler("netz", 0);
      }
    } else if (nachgeholt === "netz") {
      // Das Token ist alt, ein neues kam nicht durch — ein Netzfehler, kein Abmelden.
      throw new Fehler("netz", 0);
    } else if (schluessel === "sitzung.abgelaufen" || schluessel === "kontext.fehlt") {
      sitzungIstAus();
    }
  }

  return auswerten<T>(antwort);
}

export const hole = <T>(weg: string) => ruf<T>({ weg });
export const sende = <T>(weg: string, koerper?: unknown) => ruf<T>({ weg, art: "POST", koerper });
export const ersetze = <T>(weg: string, koerper?: unknown) => ruf<T>({ weg, art: "PUT", koerper });
export const aendere = <T>(weg: string, koerper?: unknown) => ruf<T>({ weg, art: "PATCH", koerper });
export const loesche = <T>(weg: string, koerper?: unknown) => ruf<T>({ weg, art: "DELETE", koerper });

/**
 * Die Frist im Auge behalten: Alle 120 Sekunden ein neues Token, so wie
 * es 34-api.md vorsieht. Ohne das läuft der Kontext mitten in einer
 * langen Sitzung am Brett aus, und jede Anfrage bräuchte den Umweg über
 * den Nachholversuch.
 */
export function kontextFrischHalten(): () => void {
  if (ATTRAPPE) return () => {};
  const frisch = () => { if (kontextDa()) void kontextNachholen(); };
  const uhr = window.setInterval(frisch, 120_000);
  /* Nach einem Funkloch sofort, nicht erst beim nächsten Takt: Das Token ist
     dann womöglich abgelaufen, und die erste Anfrage müsste sonst nachholen. */
  window.addEventListener("online", frisch);
  return () => {
    window.clearInterval(uhr);
    window.removeEventListener("online", frisch);
  };
}

/* ══════════════ Eine Datei holen (15.09.2026) ══════════════ */

/**
 * Holt eine Datei statt JSON — gebraucht für das Angebots-PDF.
 *
 * Warum nicht einfach ein `<a href>`? Weil das Kontext-Token im Speicher
 * liegt und nur als `Authorization`-Kopf mitgeht. Ein gewöhnlicher Link
 * schickt keinen Kopf mit und bekäme eine 401 — als leeres Fenster, ohne
 * einen Satz, der sagt warum.
 *
 * Der Nachholversuch bei abgelaufenem Kontext ist derselbe wie bei `ruf`;
 * deshalb steht das hier und nicht in einer Maske.
 *
 * Geht etwas schief, kommt **kein** Blob zurück, sondern derselbe `Fehler`
 * wie überall — mit Schlüssel, damit `lexware.kein_beleg` als Satz und
 * nicht als „geht nicht" auf dem Schirm steht.
 */
export async function holeDatei(weg: string): Promise<{ blob: Blob; name: string }> {
  let antwort: Response;
  try {
    antwort = await einmal({ weg });
  } catch {
    throw new Fehler("netz", 0);
  }

  if (antwort.status === 401) {
    const nachgeholt = await kontextNachholen();
    if (nachgeholt === "da") {
      try {
        antwort = await einmal({ weg });
      } catch {
        throw new Fehler("netz", 0);
      }
    } else if (nachgeholt === "netz") {
      throw new Fehler("netz", 0);
    }
  }

  if (!antwort.ok) {
    // Der Fehlerfall antwortet in JSON, auch wenn ein PDF erwartet war.
    const text = await antwort.text();
    let schluessel = "unbekannt";
    let daten: Record<string, unknown> = {};
    try {
      const huelle = JSON.parse(text) as ApiFehler;
      schluessel = huelle.fehler || "unbekannt";
      daten = huelle.daten ?? {};
    } catch {
      /* Keine Hülle — dann sagt der Status, was war (413, 429, sonst unlesbar). */
      schluessel = ohneHuelle(antwort.status);
    }
    throw new Fehler(schluessel, antwort.status, daten);
  }

  /* Der Dateiname steht in `Content-Disposition`. Steht dort keiner,
     bleibt der Aufrufer bei seinem eigenen — ein Name ist besser als
     „download". */
  const kopf = antwort.headers.get("Content-Disposition") ?? "";
  const treffer = /filename="?([^";]+)"?/.exec(kopf);
  return { blob: await antwort.blob(), name: treffer?.[1] ?? "" };
}
